import GridViewModel from '@/ViewModels/Controls/GridViewModel';
import ComponentManager from '@/Management/ComponentManager';
import Config from '@/Management/GlobalConfiguration';
import ResourceManager from '@/Management/ResourceManager';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import RADApplication from '@/Management/RADApplication';
import * as DateHelper from '@/Utility/DateHelper';
import FilterViewModel from '@/ViewModels/Controls/FilterViewModel';
import GenericObject from '@/Model/GenericObject';
import ColumnFactory from '@/Factories/ColumnFactory';
import Factory from '@/Factories/Factory';
import Utilities from '@/Utility/Utilities';
import * as StringFormatter from '@/Utility/StringFormatter';
import { GridColumn, GridColumnDefinition, GridColumnFactory, FilterJson, IDataGridViewModel, IDataGridViewModelConstructorParameter, CustomFilterChip } from '#/DefaultViewExtensionInterfaces';
import FilterHelper from '@/Utility/FilterHelper';
import { ISortByCol } from 'rad-coreframe-grid';
import { IHideConnectedElementsRequestDTO, IIteratorDTO } from "coreframetypings/dto";
import ListViewHelper from '@/Utility/ListViewHelper';
import User from '@/Model/User';

/**
 * Das ViewModel für die DataGrid
 */
export default class DataGridViewModel extends GridViewModel implements IDataGridViewModel {

    /**
     * Ein aktuelles Abbild der PDO-IDs aller Daten in einem Hashset
     */
    public pdoIdDataSet: KnockoutComputed<object>;

    /**
    * Gibt an, ob das Control geladen wird
    */
    public isBusy: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob es gerade erlaubt ist Daten vom Server zu beziehen oder nicht.
     */
    // Wenn wir z.B. nach dem Holen der Daten page korrigieren, wird in Grid.js diese Änderung an widget.pager gereicht, welcher hier ein neues read veranlasst
    // Dieses read wäre aber überflüssig, daher geben wir in diesem Fall die alten Daten zurück.
    public offline: boolean;

    /**
     * Informationen zum letzten Datenabruf
     */
    public lastRefresh: any;

    /**
     * Der Typ der Elemente in der Tabelle
     */
    public pdTypeName: string;

    /**
     * Definiert zur Performance-Verbesserung einen Standard-Iterator
     */
    public defaultIterator: any;

    /**
     * Der Name der Association bei einem AssociationDataGrid, ansonsten NULL
     */
    public associationName: string;

    /**
     * Der Id des Objektes bei einem AssociationDataGrid, ansonsten NULL
     */
    public associationPdoId: string;

    /**
     * Der Typ des Objektes bei einem AssociationDataGrid, ansonsten NULL
     */
    public associationType: string;

    /**
     * Handelt es sich hierbei um ein AssociationDataGrid oder eine ListView (Wichtig für die Höhe)
     */
    public isAssociationDataGrid: boolean;

    /**
     * Underdrückt einmalig das Auswerten der DataSource
     */
    public suppressOnce: boolean;

    /**
    * Der Key unter dem die ColumnConfig in UserSpecificData verwaltet wird
    */
    public storageColumnDefinitionKey: string;

    /**
     * Die PDOId des Contexts
     */
    public contextPdoId: string;

    /**
     * Gibt die Id eines Objekts an, welches initial selektiert ist
     */
    public initial: number;

    private _activeAfterFirstDisplayed;
    public activeAfterFirstDisplayed;

    /**
    * Der aktuelle Iterator
    */
    public iterator: KnockoutObservable<IIteratorDTO>;

    /**
    * Das ViewModel für den Filter
    */
    public filter: FilterViewModel;

    /**
     * Ein Vorfilter
     */
    public preFilter: KnockoutObservable<string>;

    /**
     * Der statische Filter
     */
    public staticFilter: KnockoutObservable<string>;

    /**
     * Vordefinierte Filter-Chips
     */
    public customFilterChips: KnockoutObservable<Array<CustomFilterChip>>

    /**
    * Ein Hibernate-Ausdruck zur Default-Sortierung
    */
    public defaultOrder: KnockoutObservable<string>;

    /**
     * Definiert den aktuellen Filtermodus des Mandanten
     */
    public principalFilterMode: KnockoutObservable<string>;

    /**
     * Gibt an, ob Elemente aller Mandanten angezeigt werden
     */
    public includeAllPrincipals: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob Elemente von übergeordneten Mandanten angezeigt werden
     */
    public includeBasePrincipals: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob Elemente von Untermandanten angezeigt werden
     */
    public includeSubPrincipals: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob die ListView PrincipalIndependent ist
     */
    public isPrincipalIndependent: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob alle mandantenabhängigen Objekte angezeigt werden soll
     */
    public disablePrincipalFilter: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob historische Elemente angezeigt werden
     */
    public showHistoricedObjects: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob aktuelle Elemente angezeigt werden
     */
    public showCurrentObjects: KnockoutObservable<boolean>;

    /** Wenn über ein abgeleitetes Attribut sortiert oder gefiltert wird, dann muss die QueryEngine erheblich viele Objekte in den
     * Session-Cache laden. Dies führt zu Speicherproblemen. Wenn dieses Flag gesetzt ist, wird ein speicheroptimierendes Verhalten aktiviert,
     * das die Objekte aus der Session entfernt. Der Nachteil dieses Ansatzes ist jedoch, dass dadurch in gewissen Konstellationen Lazy-Load-Exceptions
     * im User-Code entstehen können. Daher wird dieses Verhalten nur in den Listenansichten im Client aktiviert.
     */
    public memoryOptimizationEnabled: KnockoutObservable<boolean>;

    public historyDate: KnockoutObservable<any>;

    /**
     * Ungefilterte Anzahl der Elemente im Grid.
     */
    public fullExtentCount: KnockoutObservable<string>

    /**
     * Bestimmt, ob die Gesamtanzahl der Elemente angezeigt werden soll.
     */
    public extentVisible: boolean;

    /**
    * Bestimmt, ob die Spalte für die PDO_ID standardäßig hinzugefügt werden soll
    */
    protected addPdoIdColumnByDefault: boolean;

    /**
    * Gibt an, ob die Auswahl programmatisch zurückgesetzt wurde (relevant z.B. für SummaryView)
    */
    public selectionWasProgrammaticallyResetted: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Grid sich von selbst initialisieren soll
     * Bei Vererbung sollte dieser Parameter "false" sein
     */
    public autoInit: boolean;

    /**
     * Gibt an, ob das Grid vollständig initalisiert wurde, d.h. wurde der Konstruktor vollständig ausgeführt
     * (bei Verberbung auch die der Unterklassen), jedoch müssen nicht zwingend die Daten geladen sein
     */
    protected isInitialised: boolean;

    /**
     * Verhindert mehrfaches Aktualisieren des Grids bei Filteränderungen durch Debounce der Refresh-Funktion
     */
    private debounceRefresh: Cancelable<(defer : JQueryDeferred<any>) => void>;

    /*
     Rückgabewert der refresh-Funktion, Ergebnis wird in debounceRefresh bestimmt
    */
    private refreshDeferred : JQueryDeferred<any>;

    public renderGridAsync: boolean;

    private hideConnectedElementsRequest: IHideConnectedElementsRequestDTO;

    private ignoreOrder: boolean;

    private rowLimit: number;

    /**
     * Das ViewModel für die DataGrid
     * @param {object} params - Ein Objekt mit Optionen für das Control
     * @constructor
     */
    public constructor(protected params: IDataGridViewModelConstructorParameter) {
        super(params);

        this.debounceRefresh = Utilities.debounce((deferredResult) => {
            if (!this.isDisposed) {
                $.when(this.refreshImmediately())
                    .then((data) => {
                        deferredResult.resolve(data);
                    });
            }
            else
                deferredResult.resolve(false);
        }, 300, false);

        this.pdoIdDataSet = this.defineComputed(() => {
            let pdoIdDataSet = {};
            let data = this.data();

            if (data) {
                $.each(data,
                    (i, item) => {
                        pdoIdDataSet[item.PDO_ID] = true;
                    });
            }

            return pdoIdDataSet;
        });
        // Bei jedem Update der Daten belassen wir die Selektion nur auf den Datensätzen, die aktuell auch sichtbar/vorhanden sind
        this.defineSubscription(this.pdoIdDataSet,
            (newValue) => {
                let selected = this.selected(),
                    newSelected = [];

                if (!newValue || !selected) {
                    return;
                }

                $.each(selected,
                    (i, pdoId) => {
                        if (newValue.hasOwnProperty(pdoId)) {
                            newSelected.push(pdoId);
                        }
                    });
                if (!_.isEqual(this.selected(), newSelected)) {
                    this.selectionWasProgrammaticallyResetted(true);
                    this.selected(newSelected);
                }
            });
        this.selectionWasProgrammaticallyResetted = this.observableOrDefault(false);
        this.isBusy = this.observableOrDefault(params.isBusy, false);
        this.autoInit = this.paramOrDefault(params.autoInit, true);

        if (!params.isLazy) {
            this.isLazy(true);
        }

        this.offline = this.paramOrDefault(params.offline, false);
        this.lastRefresh = {
            started: 0, // Zeitpunkt des Starts der Anfrage
            finished: 0, // Zeitpunkt des Endes der Anfrage
            filter: null // Der verwendete Filterausdruck
        };
        this.pdTypeName = this.paramOrDefault(params.pdTypeName, null);
        this.defaultIterator = this.paramOrDefault(params.defaultIterator, null);
        this.pageSize = this.observableOrDefault(params.pageSize, 20);
        this.associationName = this.paramOrDefault(params.associationName, null);
        this.associationPdoId = this.paramOrDefault(params.associationPdoId, null);
        this.associationType = this.paramOrDefault(params.associationType, null);
        this.isAssociationDataGrid = this.paramOrDefault(params.isAssociationDataGrid, false);
        this.suppressOnce = this.paramOrDefault(params.suppressOnce, false);
        
        this.defaultHeight = this.observableOrDefault('100%');
        if (this.isAssociationDataGrid) {
            this.defaultHeight(Config.dataGrid.HeightForAssociationGridInDV);
        }
        this.height = this.observableOrDefault(params.height,
            this.defaultHeight(),
            (value) => {
                if (value.indexOf("%") == -1) {
                    let int = parseInt(value, 10);
                    if (isNaN(int) || int < Config.dataGrid.MinHeightForAssociationGridInDV) {
                        return Config.dataGrid.HeightForAssociationGridInDV;
                    }
                    return int + "px";
                }
                else
                    return value;
            });
        this.storageColumnDefinitionKey = this.paramOrDefault(params.storageColumnDefinitionKey, null);
        this.contextPdoId = this.paramOrDefault(params.contextPdoId, -1);
        this.initial = this.paramOrDefault(params.initial, -1);
        this._activeAfterFirstDisplayed = false;
        this.activeAfterFirstDisplayed = this.defineComputed(() => {
            if (this._activeAfterFirstDisplayed) {
                return true;
            }

            if (this.isDisplayed()) {
                this._activeAfterFirstDisplayed = true;

                // Wurde für CF-11946 auskommentiert, da diese Funktionalität störte und sowieso keine Verwendung hatte
                //if (this.isLazy()) {
                //    this.isLazy(false);
                //}
            }
            return this._activeAfterFirstDisplayed;
        });
        this.iterator = this.observableOrDefault(null);
        this.selected =
            this.observableOrDefault(params.selected, this.initial > -1 ? [this.initial] : []) as
            KnockoutObservableArray<any>;

        this.filter = this.paramOrDefault(params.filter, null);
        this.customFilterChips = this.observableOrDefault(params.customFilterChips, []);
        if (this.filter != null) {
            this.defineSubscription(this.filter.isLoading, (isLoading) => {
                if (!this.isDisposed)
                    isLoading ? this.increaseBusyCounter() : this.decreaseBusyCounter();
            });

            this.createCustomFilterChip(this.customFilterChips());
        }


        this.preFilter = this.observableOrDefault(params.preFilter, null);
        this.staticFilter = this.observableOrDefault(params.staticFilter, null);
        this.defaultOrder = this.observableOrDefault(params.defaultOrder, '');
        this.ignoreOrder = this.paramOrDefault(params.ignoreOrder, false);
        this.rowLimit = this.paramOrDefault(params.rowLimit, 0);
        this.hasPager = this.observableOrDefault(params.hasPager, true);
        this.hasSummaryRow = this.observableOrDefault(params.hasSummaryRow, true);
        this.principalFilterMode = this.observableOrDefault(params.principalFilterMode,
            '',
            (value, defaultValue) => {
                if (value === null || value === undefined) {
                    return defaultValue;
                }

                return RADApplication.getInstance().getClientValueForPrincipalFilterMode(value);
            });
        this.includeAllPrincipals = this.observableOrDefault(params.includeAllPrincipals, false);
        this.includeBasePrincipals = this.observableOrDefault(params.includeBasePrincipals, true);
        this.includeSubPrincipals = this.observableOrDefault(params.includeSubPrincipals, false);
        this.isPrincipalIndependent = this.observableOrDefault(params.isPrincipalIndependent, false);
        this.disablePrincipalFilter = this.observableOrDefault(params.disablePrincipalFilter, false);
        this.showHistoricedObjects = this.observableOrDefault(params.showHistoricedObjects, false);
        this.showCurrentObjects = this.observableOrDefault(params.showCurrentObjects, true);
        this.memoryOptimizationEnabled =
            this.observableOrDefault(params.memoryOptimizationEnabled, !this.associationName);
        this.historyDate = this.observableOrDefault(params.historyDate, null);
        this.renderGridAsync = this.paramOrDefault(params.renderGridAsync, false);

        this.hideConnectedElementsRequest = this.paramOrDefault(params.hideConnectedElementsRequest, null);

        if (!Utilities.isNullOrUndefinedOrBlank(params.staticFilter) && !Utilities.isNullOrUndefinedOrBlank(params.defaultIterator?.fullExtentCount)) {
            this.fullExtentCount = ko.observable('...');
        } else {
            this.fullExtentCount = this.observableOrDefault(params.defaultIterator?.fullExtentCount, '');
        }
        this.extentVisible = this.paramOrDefault(params.extentVisible, Config.dataGrid.ShowCountInFooter);
        // Aktualisiert das Grid wenn der Mandantenfilter gewechselt wird
        this.defineSubscription(this.principalFilterMode,
            () => {
                this.refresh();
            });
        // Aktualisiert das Grid wenn die Columns verändert werden
        this.defineSubscription(this.columns,
            () => {
                this.refresh();
            });

        this.registerShouldLoadDataEvents();

        // Die Spalten des Grids mit denen im UserSpecificData synchronisieren
        this.updateColumnsFromUserSpecificData(this.columnDefinition, this.storageColumnDefinitionKey);
        // Die Spalten des Grids mit denen im Filter synchronisieren
        if (this.filter != null) {
            (this.columns as any).sync(this.filter.columns);
        }
        
        if (this.filter != null) {
            this.defineComputed(() => {
                // Abhängigkeit
                let filter = this.getFilterExpression();

                // Abbrechen, wenn dies die initiale Evaluation zur Abhängigkeiten-Erkennung ist 
                // oder der Filter sich nicht verändert hat
                if (ko.computedContext.isInitial() || JSON.stringify(this.lastRefresh.filter) == JSON.stringify(filter)) {
                    return;
                }

                // Für den Fall das ein statischer Filter gesetzt wurde in einem Lazy Grid
                if (this.isLazy() && this.lastRefresh.filter === null)
                    return;

                this.refresh();
            });

            let originalSortingVisibility = this.filter.shouldEnableSorting();
            this.defineSubscription(this.isLimited,
                (isLimited) => {
                    if (isLimited)
                        this.filter.shouldEnableSorting(false);
                    else
                        this.filter.shouldEnableSorting(originalSortingVisibility);
                });
        }

        if (this.autoInit) {
            if (!this.isFilterReady()) {
                // Loader soll auf jeden fall angezeigt werden, bis das Grid initialisiert ist
                // Wird am  ende der init Methode wieder ausgeblendet
                this.increaseBusyCounter();
                // Computed anlegen, um auf die Erstellung der Filter Komponenten zu warten
                const isFilterReadyComputed = this.defineComputed(() => {
                    return this.isFilterReady();
                });

                this.defineSubscription(isFilterReadyComputed, (isReady) => {
                    if (isReady) {
                        this.init();
                        this.decreaseBusyCounter();
                        // nach dem initialisiern kann das computed für das einmalige warten auf die FilterKomponenten entfernt werden
                        isFilterReadyComputed.dispose();
                    }
                });
            } else {
                this.init();
            }
        }
    }

    private createCustomFilterChip(customData: Array<CustomFilterChip>) {
        if (this.isDisposed)
            return;

        this.filter.filterChipsViewModel.addCustomFilterChips(customData);
    }

    private isFilterReady(): boolean {
        const chipsVMisBusy = this.filter?.filterChipsViewModel?.isBusy();
        const filterElementsLength = this.filter?.elements().length;
        const isFilterLoadingDeactivated = this.filter?.isFilterLoadingDeactivated();
        return !chipsVMisBusy && (filterElementsLength == 0 || !isFilterLoadingDeactivated);
    }

    private registerShouldLoadDataEvents(): void {
        let isLazySubscription: KnockoutSubscription = null;
        let isDisplayedSubscription: KnockoutSubscription = null;

        const unregisterShouldLoadDataEvents = () => {
            if (!Utilities.isNullOrUndefined(isLazySubscription)) {
                isLazySubscription.dispose();
                isLazySubscription = null;
            }
            if (!Utilities.isNullOrUndefined(isDisplayedSubscription)) {
                isDisplayedSubscription.dispose();
                isDisplayedSubscription = null;
            }
        }

        const handler = () => {
            if (this.isDisposed)
                return;

            const isLazy = this.isLazy();
            if (!isLazy) {
                this.shouldLoadData(true);
            } else {
                const isDisplayed = this.isDisplayed();
                this.shouldLoadData(isDisplayed);
            }

            if (this.shouldLoadData()) {
                unregisterShouldLoadDataEvents();
            }
        }

        isLazySubscription = this.isLazy.subscribe(newValue => handler());
        isDisplayedSubscription = this.isDisplayed.subscribe(newValue => handler());
        // Initiale Ausführung
        handler();
    }

    public init(): void {
        const action = () => {
            this.refreshImmediately();
        };

        this.isInitialised = true;
        if (!this.shouldLoadData()) {
            let subscription = this.shouldLoadData.subscribe(
                (shouldLoadData) => {
                    if (!shouldLoadData)
                        return;
                    action();
                    subscription.dispose();
                });
        } else {
            action();
        }

    }

    /**
     * @override
     * @inheritdoc 
     * @param columns
     */
    protected createColumns(columns: KnockoutObservableArray<GridColumn>): KnockoutObservableArray<GridColumn> | KnockoutComputed<Array<GridColumn>> {
        this.addPdoIdColumnByDefault = true;
        return this.defineComputed(() => {
            let columns = this.columnDefinition();
            this.onBeforeColumnsComputed(columns);
            let result: Array<GridColumn> = [];
            // wenn PDO_ID bereits durch eine Oberklasse (z.B. SearchListView) in der ColumnDefinition steht, wird die Column der Oberklasse verwendet
            const showPDO = User.currentUserShouldShowPDOID() && !columns.some(x => x.name === 'PDO_ID');

            if (this.addPdoIdColumnByDefault || showPDO) {
                result.push({
                    name: 'PDO_ID',
                    field: 'PDO_ID',
                    title: 'PDO_ID',
                    sortableTitle: 'pdo_id',
                    isSortable: showPDO,
                    sortValue: "PDO_ID",
                    filterable: showPDO,
                    hidden: !showPDO,
                    value: (row) => { return row['PDO_ID']; },
                    encoded: false,
                    listViewPDOID: '0',
                    typeName: 'System.String',
                    uiGenerator: 'TextBox',
                    possiblePDEnumerationValues: {},
                    template: null
                });
            }

            const additionalColumns = this.createColumnsFromDefinition(columns);
            result.splice(1, 0, ...additionalColumns);

            return result;
        });
    }

    private createColumnsFromDefinition(definitions: GridColumnDefinition[]): GridColumn[] {
        if (definitions == null)
            return [];

        const language = (ResourceManager.getInstance().language as any).value.peek();
        const preset = this.columnPreset.peek();

        let result: GridColumn[] = [];
        for (const column of definitions) {
            const unit = column.unitNames ? column.unitNames[language] : '';
            const title = this.getTitle(column, unit, language);
            const name = column.name;
            const fieldOrName = (column.field ? column.field : column.name);
            const field = fieldOrName.replace(/\./g, '_').replace(/ListViewValue/, 'Value');
            const hidden = !column.isVisible;
            const sortable = column.sortable;
            const filterable = column.isFilterable;
            const uiGenerator = column.uiGenerator;
            const listViewPDOID = column.listViewPDOID;
            const typeName = column.dotNetTypeName;
            const encoded = true;
            const possiblePDEnumerationValues = column.possiblePDEnumerationValues;
            const factory = this.getFactory(column);
            const onAfterCellRender = column.onAfterCellRender;
            const dataClass = column.dataClass;
            const subColumns = this.createColumnsFromDefinition(column.subColumns);

            let factoryClass = null;
            let plainTextFactory = false;
            if (factory && factory.uiGenerator)
                factoryClass = ComponentManager.getFactory(factory.uiGenerator);

            if (!factoryClass || factoryClass.isPlainText)
                plainTextFactory = true;

            let width = undefined;
            // Spaltenbreiten im Storage gesetzt
            if (preset[column.name] != null) {
                width = parseInt(preset[column.name]);
            }

            let additionalWidth = 0;
            if (factoryClass && factoryClass.additionalWidth)
                additionalWidth = factoryClass.additionalWidth;

            result.push({
                name: name,
                field: field,
                title: title,
                sortableTitle: title.toLowerCase(),
                isSortable: sortable,
                filterable: filterable,
                hidden: hidden,
                dataClass: dataClass ? dataClass : (factory && factory.uiGenerator && !plainTextFactory ? 'inner-factory' : ''),
                value: (row) => this.getColumnValue(column, row, field, factory),
                onAfterCellRender: onAfterCellRender,
                sortValue: field,
                encoded: encoded,
                uiGenerator: uiGenerator,
                listViewPDOID: listViewPDOID,
                typeName: typeName,
                possiblePDEnumerationValues: possiblePDEnumerationValues,
                width: width,
                additionalWidth: additionalWidth,
                subColumns: subColumns
            });
        }

        this.addTechnicalNameForDoubleColumnTitle(result);

        return result;
    }

    protected getColumnValue(column: GridColumnDefinition, row: Object, field: string, factory: GridColumnFactory): string | JQuery | HTMLElement {
        const raw = Utilities.isFunction(column.value)
            ? column.value(row)
            : (Utilities.isNullOrUndefined(column.value)
                ? row[field]
                : column.value);
        if (factory && factory.uiGenerator) {
            const columnFactory = this.createColumnFactory(factory, raw, row, field);
            return columnFactory.getValue();
        } else {
            if (raw && raw.ListViewValue) {
                return StringFormatter.escapeHtml(raw.ListViewValue, true);
            }
            if (raw && raw.Value) {
                return StringFormatter.escapeHtml(raw.Value, true);
            }

            return StringFormatter.escapeHtml(raw, true);
        }
    }

    protected createColumnFactory(factory: GridColumnFactory, raw: any, row: Object, field: string): ColumnFactory {
        const params = $.extend(factory.parameters, {
            raw: raw,
            row: row,
            field: field
        });
        return Factory.createInstance(factory.uiGenerator, params, 'DummyColumn') as ColumnFactory;
    }

    /**
     * Fügt bei Spalten mit gleichem Titel den technischen Bezeichner zum Titel hinzu
     * um die Spalten unterscheiden zu können
     * @param columns
     */
    private addTechnicalNameForDoubleColumnTitle(columns: GridColumn[]): void {
        // CF-93467: Wenn man mehrere "leere" Spaltennamen haben möchte, so greift diese Logik hier welche schlussendlich
        // in dieser Darstellung [] endet. Daher betrachten wir hier wirklich nur die Spalten, wo der title gesetzt ist
        const relevantColumns = columns.filter(x => (!x.hidden || x.filterable) && !Utilities.isNullOrUndefinedOrBlank(x.title));
        const titleCounted = _.countBy(relevantColumns, (column) => column.title);
        const doubleColumnTitle = _.keys(titleCounted).filter(x => titleCounted[x] > 1);
        const doubleColumns = relevantColumns.filter(x => doubleColumnTitle.indexOf(x.title) > -1);
        doubleColumns.forEach(x => x.title = x.title + " [" + x.field + "]");
    }

    // @Override
    protected getFactory(column: GridColumnDefinition): GridColumnFactory {
        let typeName = column.dotNetTypeName;
        let factory = column.listViewColumnFactory ? Factory.parseUIGeneratorQuery(column.listViewColumnFactory) : null;
        if (!factory || !factory.uiGenerator) {
            if (!factory) {
                factory = {
                    uiGenerator: null,
                    parameters: null
                }
            }

            if (typeName != null && typeName.indexOf('DateTime') > -1) {
                factory.uiGenerator = 'DateTimeColumn';
            }
            else if (typeName != null && typeName.indexOf('Boolean') > -1) {
                factory.uiGenerator = typeName.indexOf('Nullable') > -1
                    ? 'CheckBoxThreeStateColumn'
                    : 'CheckBoxColumn';
            }
        }
        return factory;
    }

    public setParametersOffline(setter: Function): void {
        let _offline = this.offline;
        this.offline = true;
        try {
            setter();
        }
        finally {
            this.offline = _offline;
        }
    }

    public setBulkParameters(setter: Function): void {
        this.setParametersOffline(setter);
        this.refresh();
    }

    private fixPageAndSize(page: number, pageSize: number): void {
        this.setParametersOffline(() => {
            if (page != this.page.peek()) {
                this.page(page);
            }
            if (pageSize != this.pageSize.peek()) {
                this.pageSize(pageSize);
            }
        });
    }

    private isSuppressOnce(): boolean {
        if (this.suppressOnce) {
            this.suppressOnce = false;
            return true;
        }
        return false;
    }

    // @Override
    public onPageSizeChanged(newPageSize: number): void {
        if (this.pageSize() != newPageSize) {
            super.onPageSizeChanged(newPageSize);
            // Seitengrößenwechsel soll immer auf die erste Seite verweisen
            this.page(1);
            this.refresh();
        }
    }

    // @Override
    public onPageChanged(newPage: number): void {
        if (this.page() != newPage) {
            super.onPageChanged(newPage);
            this.refresh();
        }
    }

    // @Override
    public onSortChanged(sortExpression: ISortByCol[]): void {
        this.orderBy(sortExpression);
        this.orderBy.notifySubscribers(sortExpression);
        this.refresh();
    }

    protected onBeforeColumnsComputed(columns: Array<GridColumnDefinition>): void {
        // Hook method
    }

    /*
    * Aktualisiert das DataGrid (interne Methode)
    */
    protected refreshGrid(): JQueryPromise<any> {
        if (!this.shouldLoadData())
            return Utilities.resolveImmediately();

        let page = this.page();
        let pageSize = this.pageSize();
        let sort = this.orderBy();
        let currentTime = new Date().getTime();

        if (sort != this.orderBy()) {
            this.orderBy(sort);
        }

        let filter = this.getFilterExpression();
        let stringifiedFilter = !_.isEmpty(filter) ? JSON.stringify(filter) : '';

        this.lastRefresh.started = currentTime;
        this.lastRefresh.filter = filter;

        // Wenn das Grid erst später Daten lädt, darf der DefaultIterator nicht mehr genutzt werden,
        // da die Daten veraltet sein können. Daher direkt verwerfen und einen neuen Iterator erzeugen
        if (this.isLazy() && this.defaultIterator != null) {
            this.deleteIterator(false, this.defaultIterator);
            this.defaultIterator = null;
        }

        this.deleteIterator(false);

        const it = this.createIterator(stringifiedFilter);

        if (it == null) {
            this.increaseBusyCounter();
            $.when(this.getExtent(this.defaultIterator, this.page(), pageSize)).then((data) => {
                if (!this.isDisposed) {
                    this.data(data);
                    
                    this.lastRefresh.finished = currentTime;

                    this.iterator(this.defaultIterator);
                    this.defaultIterator = null;
                }
            }).always(() => {
                if (this.isDisposed)
                    return;
                this.decreaseBusyCounter()
            });

            return Utilities.resolveImmediately();
        }

        let savedIterator: IIteratorDTO = null;
        this.increaseBusyCounter();
        return it.then((iterator) => {
            if (!this.isDisposed) {
                this.iterator(iterator);
                savedIterator = iterator

                this.setPageFromIterator(iterator);

                this.fullExtentCount(iterator.fullExtentCount);

                return this.getExtent(iterator, this.page(), pageSize);
            }
            return $.when<any[]>(null);
        }).then((data: any[]) => {
            // bei langsamen Netzwerk kann es vorkommen, dass duch Eingabe von Filtern bereits ein neuer Iterator angelgt wurde
            if (!this.isDisposed && data && savedIterator == this.iterator()) {
                this.data(data);
                this.lastRefresh.finished = currentTime;
            }
        }).always(() => {
            if (this.isDisposed)
                return;
            this.decreaseBusyCounter();
        }).promise();
    }

    public createIterator(stringifiedFilter: string) {
        let orderby = this.getOrderExpression();
        if (!isNaN(this.rowLimit) && this.rowLimit > 0 && this.filter && this.filter.lastMaxExtentCount() > this.rowLimit)
            orderby = '';

        if (this.associationName != null) {
            if (this.defaultIterator == null) {
                return RADEnvironmentProxy.getInstance().createIteratorForAssoziation({
                    pdTypeName: this.associationType,
                    pdo_id: this.associationPdoId,
                    assoziationName: this.associationName,
                    properties: this.getProperties(),
                    filter: stringifiedFilter,
                    orderby: orderby,
                    retrieveExtentCount: true,
                    itOperator: this.getIteratorOperator()
                });
            }
            return null;
        }
        return RADEnvironmentProxy.getInstance().createIteratorWithOperator({
            pdTypeName: this.pdTypeName,
            properties: this.getProperties(),
            filter: stringifiedFilter,
            orderby: orderby,
            retrieveExtentCount: true,
            contextPDO_ID: this.contextPdoId,
            itOperator: this.getIteratorOperator()
        });
    }

    private setPageFromIterator(iterator: IIteratorDTO): void {
        if (iterator.searchObjectPositionResponse !== null) {
            const page = iterator.searchObjectPositionResponse.pageID + 1;
            this.page(page);
        }
    }

    /**
     * Gibt die Menge der Namen der Properties zurück, die für das Grid angefragt wurden
     */
    protected getProperties(): string[] {
        let result = _.map(this.getVisibleColumns(), (c: any) => {
            return c.name;
        });

        if (result.length === 0) {
            result.push('ObjectIdentValue');
        }

        return result;
    }

    /**
     * Gibt die Menge der Felder der Properties zurück, die für das Grid angefragt wurden
     */
    protected getPropertiesFieldNames(): string[] {
        let result = _.map(this.getVisibleColumns(), (c: any) => {
            return c.field;
        });

        if (result.length === 0) {
            result.push('ObjectIdentValue');
        }

        return result;
    }

    /**
     * Gibt die sichtbaren Spalten zurück
     */
    private getVisibleColumns(): GridColumn[] {
        return _.filter(this.columns(),
            (c: any) => {
                return !c.hidden;
            });
    }

    // Notiz: Alle Daten des IteratorOperator werden im HTML-Client im DataGridViewModel bzw. RADApplication gehalten
    // Setzen von principalFilterMode wirkt stärker als das Setzen der einzelnen bools 'includeAllPrincipals', 'includeSubPrincipals', 'includeBasePrincipals'
    public getIteratorOperator(): any {
        let principalFilterMode = this.principalFilterMode();
        let principal = RADApplication.getInstance().principal.name();
        let isPrincipalIndependent = this.isPrincipalIndependent();
        let includeAllPrincipals = this.includeAllPrincipals();
        let includeBasePrincipals = this.includeBasePrincipals();
        let includeSubPrincipals = this.includeSubPrincipals();
        let disablePrincipalFilter = this.disablePrincipalFilter();

        let showHistoricedObjects = this.showHistoricedObjects();
        let showCurrentObjects = this.showCurrentObjects();
        let memoryOptimizationEnabled = this.memoryOptimizationEnabled();

        let historizationDates = null;

        if (this.historyDate()) {

            let date = this.historyDate();
            if (date instanceof Date) {
                date = DateHelper.toDotNetDateString(date);
            }
            else if (typeof date == 'number') {
                date = DateHelper.ticksToString(date);
            }

            showHistoricedObjects = true;
            historizationDates = [date];
        }

        let iteratorOperator = {
            IncludeAllPrincipals: includeAllPrincipals,
            IncludeBasePrincipals: includeBasePrincipals,
            IncludeSubPrincipals: includeSubPrincipals,
            DisablePrincipalFilter: disablePrincipalFilter,
            ShowHistoricedObjects: showHistoricedObjects,
            ShowCurrentObjects: showCurrentObjects,
            MemoryOptimizationEnabled: memoryOptimizationEnabled,
            HistorizationDates: historizationDates,
            OnlySubPrincipals: null,
            SearchObjectPositionRequest: {
                PdoIDToFind: this.initial,
                CurrentPagingSize: this.pageSize()
            },
            IsGridIterator: true,
            hideConnectedElementsRequest: this.hideConnectedElementsRequest
        };

        if ((!isPrincipalIndependent && principal) || isPrincipalIndependent) {
            // TODO: PrincipalHelper nutzen
            if (principalFilterMode === 'PRINCIPAL_SELECTOR_ALL' || principalFilterMode === 'WithAllPrincipals') {
                iteratorOperator.IncludeAllPrincipals = true;
            } else if (principalFilterMode === 'PRINCIPAL_SELECTOR_ONLY_SUB' || principalFilterMode === 'OnlySubPrincipals') {
                iteratorOperator.IncludeSubPrincipals = false;
                iteratorOperator.OnlySubPrincipals = true;
            } else if (principalFilterMode === 'PRINCIPAL_SELECTOR_CURRENT_AND_SUB' || principalFilterMode === 'WithSubPrincipals') {
                iteratorOperator.IncludeSubPrincipals = true;
                iteratorOperator.OnlySubPrincipals = false;
            } else if (principalFilterMode === 'PRINCIPAL_SELECTOR_WITH_BASE' || principalFilterMode === 'OnlySubPrincipals') {
                iteratorOperator.IncludeBasePrincipals = true;
            } else if (principalFilterMode === 'PRINCIPAL_SELECTOR_ONLY_CURRENT' || principalFilterMode === 'Default') {
                iteratorOperator.IncludeSubPrincipals = false;
                iteratorOperator.OnlySubPrincipals = false;
                iteratorOperator.IncludeBasePrincipals = false;
            }
        }

        return iteratorOperator;
    }

    protected calcStartPoint(page: number, pageSize: number): number {
        let start = (page - 1) * pageSize;
        while (start >= this.realTotalRows()) {
            if (page <= 0) {
                break;
            }
            start = this.calcStartPoint(--page, pageSize);
        }

        return start;
    }

    protected calcEndPoint(start: number, pageSize: number): number {
        let end = start + pageSize - 1;
        if (end < 0)
            end = 0;

        return end;
    }

    private getExtent(iterator: IIteratorDTO, page: number, pageSize: number): JQueryPromise<Array<any>> | Array<any> {
        let totalRows = iterator.extentCount == "-1" ? 0 : parseInt(iterator.extentCount);
        if (iterator.isLimited) {
            this.realTotalRows(totalRows);
            this.totalRows(this.rowLimit);
        } else {
            this.realTotalRows(totalRows);
            this.totalRows(totalRows);
        }

        if (this.filter) {
            this.filter.persistExtentCountForActiveChips(this.realTotalRows());
        }
        
        if (parseInt(iterator.extentCount) > 0) {
            let initPage = page;
            const start = this.calcStartPoint(page, pageSize);
            const calculatedPage = (start / pageSize) + 1;

            if (initPage !== calculatedPage) {
                this.page(calculatedPage);
            }

            const end = this.calcEndPoint(start, pageSize);

            return RADEnvironmentProxy.getInstance().getExtentWithPDOIDAsFirstColumn({
                iteratorID: iterator.id,
                start: start,
                end: end
            }).then((arrayOfGeneric) => {
                let data = [];
                (data as any).extent = iterator.extentCount;

                if (!this.isDisposed) {
                    data = this.convertArrayOfGeneric(arrayOfGeneric);
                }

                return data;
            }).promise();
        } else {
            return [];
        }
    }

    /**
     * Konvertiert das zweideminsionale Array, in dem nur die flachen Werte enthalten sind, in eine Objektstruktur
     * @param arrayOfGeneric
     */
    protected convertArrayOfGeneric(arrayOfGeneric: Array<Array<any>>): Array<any> {
        let data = [];
        if (arrayOfGeneric == null)
            return data;

        const propertiesFieldNames = this.getPropertiesFieldNames();
        if (this.addPdoIdColumnByDefault || User.currentUserShouldShowPDOID)
            propertiesFieldNames.splice(0, 0, 'PDO_ID');
        for (let i = 0; i < arrayOfGeneric.length; i++) {
            const result = {};
            const currentRow = arrayOfGeneric[i];
            for (let j = 0; j < currentRow.length; j++) {
                result[propertiesFieldNames[j]] = currentRow[j];
            }
            data.push(result);
        }
        return data;
    }

    public getIteratorId(): string {
        let it: any = this.iterator.peek();
        if (it == null) {
            return null;
        }
        return it.id;
    }

     /**
     * Liefert anhand des vom Benutzer gesetzten Filters und des statischen Filters einen Filterausdruck
     */
    public getFilterExpression(useThisKeyword?: boolean): FilterJson {
        return FilterHelper.createFilterObject(this.staticFilter(), this.preFilter(), this.filter, this.customFilterChips());
    }

    /**
     * Liefert anhand der vom Benutzer gewählten Sortierung und der Standard-Sortierung einen Sortierausdruck,
     * sofern die Sortierung nicht explizit ignoriert werden soll
     */
    public getOrderExpression(): string {
        if (this.ignoreOrder)
            return '';

        return FilterHelper.createOrderExpression(this.orderBy(), this.columns(), this.associationName, this.defaultOrder());
    }

    /**
    * Liefert ein GenericObject für ein Element des DataGrids
    */
    public getGenericObject(pdoId: string, properties: any): JQueryPromise<GenericObject> {
        return RADEnvironmentProxy.getInstance().getObject({
            pdTypeName: this.pdTypeName,
            pdoID: pdoId,
            properties: properties
        }).then((subject) => {
            return new GenericObject(subject as any);
        }).promise();
    }
    
    /**
     * Aktualisiert das Datagrid ohne "debouncing"
     */
    public refreshImmediately(): JQueryPromise<any> {
        this.increaseBusyCounter();
        let result = this.refreshGrid();
        this.decreaseBusyCounter();

        // Initial zurücksetzen, damit dieses beim Refresh nicht mehr für getIteratorOperator herangzeogen wird
        this.initial = -1;

        return result;
    }

    /**
     * Aktualisiert das DataGrid mit "debouncing"
     */
    public refresh(): JQueryPromise<any> {
        if (this.isSuppressOnce() || this.preventRefresh || this.offline || !this.isInitialised) {
            this.preventRefresh = false;
            return Utilities.resolveImmediately();
        }

        this.increaseBusyCounter();

        // Wenn wir aktuell auf kein Ergebnis warten -> neues deferred erstellen, sonst wird altes wiederverwendet
        if (Utilities.isNullOrUndefined(this.refreshDeferred) || this.refreshDeferred.state() != "pending")
            this.refreshDeferred = $.Deferred();       

        this.debounceRefresh(this.refreshDeferred);

        return this.refreshDeferred
            .always(() => {
                if (!this.isDisposed)
                    this.decreaseBusyCounter();
            })
            .promise();
    }

    private deleteIterator(clearClientIteratorToo: boolean, anotherIterator?: IIteratorDTO): void {
        if (this.isDisposed) {
            return;
        }

        const iterator = anotherIterator || this.iterator.peek();
        // wir wollen hierdurch (falls dies im Rahmen eines Computed aufgerufen wird)
        // kein Dependency zu self.iterator erschaffen; daher verwenden wir .peek()

        if (iterator != null) {
            RADEnvironmentProxy.getInstance().deleteIterator({
                iteratorID: iterator.id
            }).then(() => {
                if (!this.isDisposed && clearClientIteratorToo) {
                    this.iterator(null);
                    this.defaultIterator = null;
                }
            });
        }
    }

    private updateColumnsFromUserSpecificData(columnDefinition: KnockoutObservableArray<GridColumnDefinition>, storageColumnDefinitionKey: string): void {
        if (storageColumnDefinitionKey != null && RADApplication.getInstance().user.existUserSpecificData(storageColumnDefinitionKey)) {
            let columnDef = columnDefinition();
            let columnUSD = RADApplication.getInstance().user.getUserSpecificData(storageColumnDefinitionKey);

            if (columnUSD().indexOf('$|$') < 0) {
                // Bei altem UserSpecificData Format werden die Columns zusammengesetzt aus Standard und LocalStorage verwendet
                this.setUserSpecificData(storageColumnDefinitionKey, columnDef);
                return;
            }

            let columnsObj = this.createColumnsObjectFromDefinition(columnDef);

            if (ListViewHelper.createColumnDefinitionString(columnsObj.visibleColumns, columnsObj.invisibleColumns) !== columnUSD()) {
                // Anpassen des Storage durch UserSpecificData, wenn Unterschiede vorhanden sind
                const uspColumns = this.parseUSPString(columnUSD());
                const newStandardColumns = columnsObj.visibleColumns.filter(c => uspColumns.visibleColumns.indexOf(c) < 0 && uspColumns.invisibleColumns.indexOf(c) < 0);

                let storageColumns = [];
                // Bei neuer nicht optionaler Spalte USP verwerfen und Standard-Konfiguration übernehmen
                if (newStandardColumns.length > 0) {
                    storageColumns = columnDef;
                } else {
                    storageColumns = this.createColumnsForStorageFromUSP(uspColumns, columnDef);
                    columnDefinition(storageColumns);
                }
                this.setUserSpecificData(storageColumnDefinitionKey, storageColumns);
            }
        }
    }

    private parseUSPString(data: string): {visibleColumns: Array<string>, invisibleColumns: Array<string>} {
        let visibleColumns = [];
        let invisibleColumns = [];
        let word = '';
        let isEscaped = false;
        let isVisible = true;
        for (let i = 0; i < data.length; i++) {
            let c = data[i];
            if (c == ',') {
                if (!isEscaped) {
                    // current word is finished
                    if (isVisible) {
                        visibleColumns.push(word);
                    } else {
                        invisibleColumns.push(word);
                    }
                    word = '';
                    continue;
                }
            }
            else if (c === '$') {
                if (!isEscaped) {
                    if (data.substr(i, 3) === '$$[') {
                        isEscaped = true;
                        i = i + 2;
                        continue;
                    }
                    if (data.substr(i, 3) === '$|$') {
                        //current word is finished and listing of invisible columns starts
                        if (word) {
                            visibleColumns.push(word);
                            word = '';
                            isVisible = false;
                        }
                        i = i + 2;
                        continue;
                    }
                }
            }
            else if (c === ']') {
                if (isEscaped && data.substr(i, 3) === ']$$') {
                    isEscaped = false;
                    i = i + 2;
                    continue;
                }
            }

            word = word + c;
        }

        if (word) {
            if (isVisible) {
                visibleColumns.push(word);
            } else {
                invisibleColumns.push(word);
            }
        }

        return { visibleColumns: visibleColumns, invisibleColumns: invisibleColumns };
    }

    private createColumnsForStorageFromUSP(columnUSD: { visibleColumns: Array<string>, invisibleColumns: Array<string> }, columns: Array<GridColumnDefinition>): Array<GridColumnDefinition> {
        let newColDef = [];
        let visibleUnsorted = [];
        let visibleNew = [];
        let invisible = [];

        // sichtbarkeit korrigieren
        _.each(columns, (column: GridColumnDefinition) => {
            if (_.indexOf(columnUSD.visibleColumns, column.name) >= 0) {
                column.isVisible = true;
                visibleUnsorted.push(column);
            }
            else if (_.indexOf(columnUSD.invisibleColumns, column.name) >= 0) {
                column.isVisible = false;
                invisible.push(column);
            }
            else if (column.isVisible) {
                visibleNew.push(column);
            }
            else {
                invisible.push(column);
            }
        });

        // sichtbaren sortieren
        _.each(columnUSD.visibleColumns, (columnName) => {

            let col = _.find(visibleUnsorted, (column) => {
                return column.name === columnName;
            });
            if (col != undefined) {
                newColDef.push(col);
            }
        });
        _.each(visibleNew, (column) => {
            newColDef.push(column);
        });
        _.each(invisible, (column) => {
            newColDef.push(column);
        });

        return newColDef;
    }

    private createColumnsObjectFromDefinition(columnDef: Array<GridColumnDefinition>): { visibleColumns: Array<string>, invisibleColumns: Array<string> } {
        let visibleColumns = [];
        let invisibleColumns = [];
        _.each(columnDef, (column: any) => {
            if (column.isVisible) {
                visibleColumns.push(column.name);
            } else {
                invisibleColumns.push(column.name);
            }
        });

        return { visibleColumns: visibleColumns, invisibleColumns: invisibleColumns };
    }

    private setUserSpecificData(key: string, columns: Array<GridColumnDefinition>): void {
        const visibleColumns = columns.filter(x => x.isVisible ).map(x => x.name);
        const invisibleColumns = columns.filter(x => !x.isVisible).map(x => x.name);
        const columnString = ListViewHelper.createColumnDefinitionString(visibleColumns, invisibleColumns);

        if (visibleColumns.length == 0) {
            RADApplication.getInstance().user.setUserSpecificData(key, null);
        }
        else {
            RADApplication.getInstance().user.setUserSpecificData(key, columnString);
        }
    }

    public getExtendedColumns(): any[] {
        return [];
    }

    public getIteratorParameter(): string {
        if (!this.isAssociationDataGrid) {
            return "false|" + this.pdTypeName;
        }
        else {
            return "true|" + this.pdTypeName + "|" + this.associationPdoId + "|" + this.associationName;
        }
    }

    protected disposeInternal(): void {
        if (Utilities.isFunction(this.debounceRefresh?.cancel))
            this.debounceRefresh?.cancel();

        this.deleteIterator(true);
        super.disposeInternal();
    }
}

ComponentManager.setViewModel('DataGridViewModel', DataGridViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');